<script setup lang="ts">
//#region imports
import { computed, nextTick, ref, watch } from 'vue';
import { cloneDeep } from 'lodash-es';
import { useTypedStore } from '@/store';
import { useApi } from '@/api';
import { typeGuard, type EmitsToListeners , type ErrorResponse, type ToErrorMap, type VuePropType } from '@/types/core';
import FeesBlock from './FeesBlock.vue';
import type { FeesIndex } from '../Fees.vue';
import type { PutControlTowerFeesBySiteIdData } from '@/types/openapi-ts';
//#endregion

//#region types
export type PutControlTowerFeesBySiteIdErrors = ToErrorMap<PutControlTowerFeesBySiteIdData['body']>;

interface Props {
  fees?: FeesIndex
  brandId?: string,
}

interface  Emits {
  'close': [fees: any],
  'success': [fees: any],
  'cancel': []
};

export interface EditFeesParams {
  data?: Props;
  listeners?: EmitsToListeners<Emits>
}
//#endregion

//#region define
const props = withDefaults(defineProps<Props>(), {
  brandId: '',
});

const emit = defineEmits<Emits>();
//#endregion

//#region state
const store = useTypedStore();
const api = useApi();
const isVisible = ref<boolean>(false);
const errors = ref<PutControlTowerFeesBySiteIdErrors>({});
const popupFees = ref({} as FeesIndex);
//#endregion

const brands = computed(() => store.getters['misc/brands']);
const activeBrand = computed(() => {
  const { id= '', name = '', favicon_base64 = '' } = brands.value.find((brand) => brand.id === props.brandId) || {};

  return {
    id,
    name,
    favicon_base64,
  };
});

//#region methods
const open = () => {
  isVisible.value = true;
  popupFees.value = cloneDeep(props.fees!);
};

const cancel = () => {
  emit('cancel');
  isVisible.value = false;
};

const handleClose = () => {
  emit('close', popupFees.value);
  isVisible.value = false;
};

const handleSave = () => {
  api.updateFees(props.brandId, popupFees.value).then(() => {
    emit('success', popupFees.value);
    handleClose();
  }).catch((e) => {
    if(typeGuard<ErrorResponse<PutControlTowerFeesBySiteIdErrors>>(e)){
      errors.value = e?.data?.errors || {};
    }
  });
};

const removeCountry: VuePropType<typeof FeesBlock, 'onRemoveCountry'> = (i, field) => {
  const levels = [...field.custom_fees];
  const typedField  = field.field! as keyof PutControlTowerFeesBySiteIdData['body'];
  levels.splice(i, 1);
  if (errors?.value?.[typedField]?.custom_fees) {
    errors?.value?.[typedField]?.custom_fees.splice(i, 1);
  }
  field.custom_fees = levels;
};

const addCountry: VuePropType<typeof FeesBlock, 'onAddCountry'> = (e, field) => {
  field.custom_fees = [
    ...field.custom_fees, {
      fee_percent: 0,
    } as typeof field.custom_fees[number],
  ];
  nextTick(() => {
    (e.target as HTMLElement)
      ?.closest('.fees-block')
      ?.getElementsByClassName('fees-block__content')[0]
      ?.lastElementChild
      ?.scrollIntoView({ block: 'end', behavior: 'smooth' });
  });
};
//#endregion

//#region watch
watch(() => isVisible.value, async (value) => {
  if (!value) {
    emit('close', popupFees.value);
  }
});
//#endregion

defineExpose({
  open,
  handleClose,
});
</script>

<template>
  <Popup
    v-model:is-visible="isVisible"
    class="edit-fees-popup"
    :title="$t('settings.fees.edit_fees')"
    :width="715"
    @close="handleClose"
  >
    <template v-slot:body>
      <div class="edit-fees-popup__brand">
        <img
          v-if="activeBrand.favicon_base64"
          :src="activeBrand.favicon_base64"
          :alt="activeBrand.name"
          class="edit-fees-popup__brand-img"
        >
        {{ activeBrand.name }}
      </div>
      <UiScrollbar
        wrap-style="max-height: 60vh; height: auto;"
        :native="false"
      >
        <div
          class="t:grid t:grid-cols-2 t:gap-16"
        >
          <FeesBlock
            v-for="item in popupFees"
            :key="item.field"
            :fee="item"
            :errors="errors[item.field! as keyof PutControlTowerFeesBySiteIdData['body']]"
            @add-country="addCountry($event, item)"
            @remove-country="removeCountry"
          />
        </div>
      </UiScrollbar>
    </template>
    <template v-slot:footer>
      <div class="t:justify-end t:flex t:space-x-10">
        <UiButton
          type="negative"
          size="big"
          icon="times"
          @click="handleClose"
        >
          {{ $t('affiliates.offer_popup.confirm_close.closeAction') }}
        </UiButton>
        <UiButton
          filled
          size="big"
          icon="check"
          @click="handleSave"
        >
          {{ $t('dashboards.other.save_changes') }}
        </UiButton>
      </div>
    </template>
  </Popup>
</template>

<style lang="scss">
.edit-fees-popup {
  &__brand {
    display: flex;
    align-items: center;
    margin-bottom: 16px;
    font-weight: 500;
  }
  &__brand-img {
    width: 16px;
    height: 16px;
    margin-right: 8px;
    border-radius: 4px;
  }
}
</style>

